import FilterIcon from '@/assets/images/FilterIcon.png';
import { Button, ButtonProps, buttonVariants } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn, getQueryParam, updateQueryParam } from '@/lib/utils';
import { ColumnType } from '@/Types/global';
import { cx } from 'class-variance-authority';
import { debounce, uniqueId } from 'lodash';
import { ChevronLeft, ChevronRight, MoreHorizontal, Search } from 'lucide-react';

import * as React from 'react';
import { DatePicker } from './date-picker';
import { Input } from './input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { Separator } from './separator';
import { Skeleton } from './skeleton';

const Tables = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <div className='relative w-full overflow-auto rounded-xl'>
      <table
        ref={ref}
        className={cn('w-full caption-bottom text-sm  rounded-[8px]', className)}
        {...props}
      />
    </div>
  ),
);
Tables.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn('[&_tr]:border-b ', className)} {...props} />
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn('[&_tr:last-child]:border-0', className)} {...props} />
));
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn('border-t bg-white font-medium [&>tr]:last:border-b-0', className)}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn('border-b transition-colors data-[state=selected]:bg-muted', className)}
      {...props}
    />
  ),
);
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      'h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0',
      className,
    )}
    {...props}
  />
));
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn('p-4 align-middle [&:has([role=checkbox])]:pr-0', className)}
    {...props}
  />
));
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption ref={ref} className={cn('mt-4 text-sm text-muted-foreground', className)} {...props} />
));
TableCaption.displayName = 'TableCaption';

const PaginationComp = ({ className, ...props }: React.ComponentProps<'nav'>) => (
  <nav
    role='navigation'
    aria-label='pagination'
    className={cn('mx-auto flex w-full justify-center py-8', className)}
    {...props}
  />
);
PaginationComp.displayName = 'Pagination';

const PaginationContent = React.forwardRef<HTMLUListElement, React.ComponentProps<'ul'>>(
  ({ className, ...props }, ref) => (
    <ul ref={ref} className={cn('flex flex-row items-center gap-1', className)} {...props} />
  ),
);
PaginationContent.displayName = 'PaginationContent';

const PaginationItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(
  ({ className, ...props }, ref) => (
    <li ref={ref} className={cn('duration-200 transition-all', className)} {...props} />
  ),
);
PaginationItem.displayName = 'PaginationItem';

type PaginationLinkProps = {
  isActive?: boolean;
} & Pick<ButtonProps, 'size'> &
  React.ComponentProps<'a'>;

const PaginationLink = ({ className, isActive, size = 'icon', ...props }: PaginationLinkProps) => (
  <a
    aria-current={isActive ? 'page' : undefined}
    role='button'
    tabIndex={isActive ? -1 : 0}
    className={cn(
      buttonVariants({
        variant: isActive ? 'outline' : 'ghost',
        size,
      }),
      className,
      isActive ? 'bg-[#E9CC7C] text-black hover:bg-[#edd288]  rounded-sm  w-[33px] h-[25px]' : '',
      'cursor-pointer',
    )}
    {...props}
  />
);
PaginationLink.displayName = 'PaginationLink';

const PaginationPrevious = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label='Go to previous page'
    size='default'
    className={cn('gap-1 pl-2.5', className)}
    {...props}
  >
    <ChevronLeft className='h-4 w-4' />
    <span>Previous</span>
  </PaginationLink>
);
PaginationPrevious.displayName = 'PaginationPrevious';

const PaginationNext = ({ className, ...props }: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label='Go to next page'
    size='default'
    className={cn('gap-1 pr-2.5', className)}
    {...props}
  >
    <span>Next</span>
    <ChevronRight className='h-4 w-4' />
  </PaginationLink>
);
PaginationNext.displayName = 'PaginationNext';

const PaginationEllipsis = ({ className, ...props }: React.ComponentProps<'span'>) => (
  <span
    aria-hidden
    className={cn('flex h-9 w-9 items-center justify-center', className)}
    {...props}
  >
    <MoreHorizontal className='h-4 w-4' />
    <span className='sr-only'>More pages</span>
  </span>
);
PaginationEllipsis.displayName = 'PaginationEllipsis';

type FilterValue = string | number | undefined;

// select component
export interface SelectProps {
  id: string;
  label: string;
  fieldName: string;
  type?: 'select' | 'date';
  values?: {
    text: string;
    value: string;
  }[];
  filterValues?: {
    [key: string]: FilterValue;
  };
  setFilter?: (key: string | undefined, value: string | undefined, clear?: boolean) => void;
}

interface CustomSelectProps {
  tableID: string;
  id: string;
  label: string;
  fieldName: string;
  type?: 'select' | 'date';
  values?: {
    text: string;
    value: string;
  }[];
  filterValues?: {
    [key: string]: FilterValue;
  };
  setFilter?: (key: string | undefined, value: string | undefined, clear?: boolean) => void;
}

export const CustomSelect: React.FC<CustomSelectProps> = ({
  tableID,
  id,
  label,
  values,
  filterValues,
  setFilter,
  ...props
}) => {
  // clear the selected value
  const clearSelected = () => {
    if (setFilter) setFilter(id, undefined);
    updateQueryParam(`${tableID}-${id}`, '');
  };

  // select change handler
  const onSelectChange = (value: string) => {
    updateQueryParam(`${tableID}-${id}`, value);
    updateQueryParam(`${tableID}-page`, '');
    if (setFilter) setFilter(id, value);
    if (setFilter) setFilter('page', '0');
  };

  return (
    <Select onValueChange={onSelectChange} value={filterValues?.[id]?.toString() ?? ''} {...props}>
      <SelectTrigger
        onClear={clearSelected}
        id={id}
        data-testid={`${id}-data-testid`}
        className={cn(
          'h-[61px] relative p-[10px]',
          filterValues?.[id] ? ' items-end  p-[10px]' : '',
        )}
        label={label}
        selectValue={filterValues?.[id]}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {values?.map((item) => (
          <SelectItem key={item?.value} value={item?.value}>
            {item?.text}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

// interface DateProp {
//   tableID: string;
//   id: string;
//   label: string;
//   filterValues?: {
//     [key: string]: FilterValue;
//   };
//   setFilter?: (key: string | undefined, value: string | undefined, clear?: boolean) => void;
// }

// const CustomDatePicker = ({ id, label, tableID, filterValues, setFilter }: DateProp) => {
//   const clearSelected = () => {
//     if (setFilter) setFilter(id, undefined);
//     updateQueryParam(`${tableID}-${id}`, '');
//   };

//   // select change handler
//   const onDateChange = (value?: Date) => {
//     if (value) {
//       updateQueryParam(`${tableID}-${id}`, value?.toISOString());
//       updateQueryParam(`${tableID}-page`, '');
//       if (setFilter) setFilter(id, value?.toISOString());
//       if (setFilter) setFilter(`page`, '0');
//     }
//   };

//   return (
//     <DatePicker
//       onChange={onDateChange}
//       onClear={clearSelected}
//       selectedDate={filterValues?.[id] ? new Date(filterValues?.[id] || '') : undefined}
//       placeholder={label}
//     />
//   );
// };

// Pagination component
interface PaginationProps {
  dataCount?: number;
  onChange?: (value: number) => void;
  currentPage?: number;
  siblingNumber?: number;
  itemsPerPage?: number;
  id: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage = 1,
  dataCount = 0,
  onChange,
  siblingNumber = 3,
  itemsPerPage = 10,
  id,
}) => {
  const totalPages = Math.ceil(dataCount / itemsPerPage);
  const handleClick = (page: number) => {
    if (onChange) {
      updateQueryParam(`${id}-page`, page);
      onChange(page);
    }
  };

  React.useEffect(() => {
    if (onChange) onChange(+(getQueryParam(`${id}-page`) || 1));
  }, []);

  const renderPageNumbers = () => {
    const pageNumbers = [];
    for (let i = 1; i <= totalPages; i++) {
      // Display only a certain number of page links around the current page
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - siblingNumber && i <= currentPage + siblingNumber)
      ) {
        pageNumbers.push(
          <PaginationItem key={i}>
            <PaginationLink isActive={i === currentPage} onClick={() => handleClick(i)}>
              {i}
            </PaginationLink>
          </PaginationItem>,
        );
      } else if (i === currentPage - siblingNumber - 1 || i === currentPage + siblingNumber + 1) {
        // Display ellipsis before or after the hidden pages
        pageNumbers.push(
          <PaginationItem key={i}>
            <PaginationEllipsis />
          </PaginationItem>,
        );
      }
    }
    return pageNumbers;
  };

  return (
    <PaginationComp>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            onClick={() => handleClick(currentPage - 1)}
            aria-disabled={currentPage === 1}
            tabIndex={currentPage === 1 ? -1 : 0}
            className={currentPage === 1 ? 'pointer-events-none opacity-50' : undefined}
          />
        </PaginationItem>
        {renderPageNumbers()}
        <PaginationItem>
          <PaginationNext
            onClick={() => handleClick(currentPage + 1)}
            aria-disabled={currentPage === totalPages}
            tabIndex={currentPage === totalPages ? -1 : 0}
            className={currentPage === totalPages ? 'pointer-events-none opacity-50' : undefined}
          />
        </PaginationItem>
      </PaginationContent>
    </PaginationComp>
  );
};

interface TableHeaderProps {
  id: string;
  loading?: boolean;
  text?: string;
  searchPlaceholder?: string;
  headerButtons?: Array<React.ReactNode>;
  filters?: Array<React.ReactNode>;
  filterValues?: {
    [key: string]: FilterValue;
  };
  handleSearch: (value: string) => void;
  searchValue: string | undefined;
  startDate?: Date;
  endDate?: Date;
  onDateFilterChange?: (startDate: Date | undefined, endDate: Date | undefined) => void;
  tableHeading?:string;
}

const TableCustomHeader: React.FC<TableHeaderProps> = ({
  loading,
  text,
  searchPlaceholder,
  headerButtons,
  searchValue,
  filters,
  handleSearch,
  startDate,
  endDate,
  onDateFilterChange,
  tableHeading
}) => {
  // clear the filter values
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const [localStartDate, setLocalStartDate] = React.useState<Date | undefined>(startDate);
  const [localEndDate, setLocalEndDate] = React.useState<Date | undefined>(endDate);

  const clearFilter = () => {
    setLocalStartDate(undefined);
    setLocalEndDate(undefined);
    if (onDateFilterChange) onDateFilterChange(undefined, undefined);
  };
  React.useEffect(() => {
    setLocalStartDate(startDate);
    setLocalEndDate(endDate);
  }, [startDate, endDate]);

  const handleApply = () => {
    if (onDateFilterChange) {
      onDateFilterChange(localStartDate, localEndDate);
    }
    setIsPopoverOpen(false);
  };

  return (
    <>
      <div className={cx('flex items-center gap-5 w-0.5-full pb-5')}>
        <div className='flex items-start gap-4 flex-col'>
          <div className='font-semibold text-[24px]'>
            {loading ? <Skeleton className='h-9 w-[130px]' /> : text}
           
          </div>
          <span className='text-[#A8ADB9]'>{tableHeading}</span>
        </div>
        <div className={'flex items-center gap-3 grow'}>
          <div className='flex items-center gap-3'>
            {(filters || []).map((Filter, index) => (
              <React.Fragment key={index}>{Filter}</React.Fragment>
            ))}
          </div>
          <div className='flex w-[280px] relative gap-3 shrink-0 ml-auto'>
            <Button id='search' variant='ghost' size='icon' className='absolute top-1'>
              <Search
                size={20}
                className='absolute left-3 top-1/2 transform -translate-y-1/2 text-white h-5 w-5 font-bold'
              />
            </Button>
            <Input
              id='search'
              type='search'
              className={
                'h-12 w-full rounded-full pl-10 pr-5 border-none bg-[#212134] placeholder-gray-400 text-sm'
              }
              placeholder={searchPlaceholder ?? 'Search'}
              onChange={(e) => {
                handleSearch(e.target.value);
              }}
              value={searchValue ?? ''}
            />
            <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
              <PopoverTrigger asChild>
                <Button id='filter' variant='ghost' size='icon' className='absolute right-1 top-1'>
                  <img src={FilterIcon} alt='FilterIcon' className='w-11 h-11' />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                side='bottom'
                align='end'
                className='bg-[#151525] text-white rounded shadow-md border-none p-8 mr-5 flex flex-col min-w-[415px] gap-6'
                style={{
                  boxShadow: '5px 8px 40.3px 0px #00000040',
                  borderTopLeftRadius: '16px',
                  borderBottomRightRadius: '16px',
                  borderBottomLeftRadius: '16px',
                }}
              >
                <div className='flex flex-col gap-2'>
                  <div className='flex flex-col  gap-4 py-4 items-start'>
                    <span className='font-semibold text-base'>Filter by date</span>
                    <div className='flex flex-col gap-[14px] w-full'>
                      <DatePicker
                        placeholder='Start Date'
                        showCalanderIcon
                        selectedDate={localStartDate}
                        onChange={setLocalStartDate}
                        clearIcon={false}
                        format='MM/DD/YY'
                      />
                      <DatePicker
                        placeholder='End Date'
                        showCalanderIcon
                        selectedDate={localEndDate}
                        onChange={setLocalEndDate}
                        clearIcon={false}
                        format='MM/DD/YY'
                      />
                    </div>
                  </div>
                  <Separator className='h-[1px] bg-[#FFFFFF21]' />
                  <div className='flex flex-row justify-between items-center'>
                    <span
                      className='font-normal text-base underline cursor-pointer'
                      onClick={clearFilter}
                    >
                      Clear filter
                    </span>
                    <Button id='Apply-btn' size='lg' onClick={handleApply}>
                      Apply
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <div className='flex items-center gap-2'>
          {(headerButtons || []).map((HeaderButton, index) => (
            <React.Fragment key={index}>{HeaderButton}</React.Fragment>
          ))}
        </div>
      </div>
    </>
  );
};

// table component
interface TablePropsTypes {
  id: string;
  columns: ColumnType[];
  data?: {
    onClick?: () => void;
    className?: string;
    cells: {
      [key: string]: React.ReactNode;
    };
    id?: string;
  }[];
  loading?: boolean;
  dataCount?: number;
  onPageChange?: (value: number) => void;
  siblingNumber?: number;
  itemsPerPage?: number;
  text?: string;
  tableHeader?: boolean;
  searchPlaceholder?: string;
  filters?: Array<React.ReactNode>;
  filterValues?: {
    [key: string]: string | number | undefined;
  };
  setFilterValues?: React.Dispatch<
    React.SetStateAction<{
      [key: string]: string | number | undefined;
    }>
  >;
  headerButtons?: Array<React.ReactNode>;
  toggleColumn?: (id: string | undefined) => void;
  noPadding?: boolean;
  toggleView?: boolean;
  onSearchChange?: (value: string) => void;
  page?: number;
  startDate?: Date;
  endDate?: Date;
  onDateFilterChange?: (startDate: Date | undefined, endDate: Date | undefined) => void;
  heading?: string;
}

// const closeDropdown = () => {
//   document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
// };

const Table: React.FC<TablePropsTypes> = ({
  id,
  columns,
  data,
  dataCount = 10,
  itemsPerPage = 10,
  loading,
  siblingNumber,
  text,
  tableHeader,
  searchPlaceholder,
  filters,
  filterValues,
  onSearchChange,
  onPageChange,
  headerButtons,
  noPadding,
  page,
  startDate,
  endDate,
  onDateFilterChange,
  heading,
}) => {
  const [searchValue, setSearchValue] = React.useState<string>();
  const tableID = React.useMemo(() => {
    const strId = String(id);

    // Take the first two characters of the id
    const twoLetters = strId.slice(0, 2)?.toUpperCase();

    return twoLetters;
  }, [id]);

  // column count
  const columnCount = React.useMemo(() => {
    return columns.length;
  }, [columns]);

  const debouncedSearch = debounce((value) => {
    onSearchChange && onSearchChange(value);
  }, 400);
  const handleSearch = (value: string) => {
    setSearchValue(value);
    debouncedSearch(value);
  };

  return (
    <div className={cn(noPadding ? 'py-5' : 'py-9 px-11')}>
      {tableHeader && (
        <TableCustomHeader
          id={tableID}
          loading={loading}
          searchPlaceholder={searchPlaceholder}
          text={text}
          searchValue={searchValue}
          handleSearch={handleSearch}
          filterValues={filterValues}
          filters={filters}
          headerButtons={headerButtons}
          startDate={startDate}
          endDate={endDate}
          onDateFilterChange={onDateFilterChange}
          tableHeading={heading}

        />
      )}
      <div className='border-[#343840] border-[1px] white rounded-[13px]'>
        <Tables id={tableID} data-testid={`${tableID}-testid`}>
          <TableHeader className='bg-[#151525]'>
            <TableRow className='bg-[#151525] border-[#343840]'>
              {columns?.map(
                (cell) =>
                  (cell.isVisible || cell.id === 'action') && (
                    <TableHead
                      key={uniqueId('table_head_')}
                      className={cn(
                        cell.className,
                        'text-white font-semibold text-sm',
                        cell.align ? `text-${cell.align}` : undefined,
                      )}
                    >
                      {cell.title}
                    </TableHead>
                  ),
              )}
            </TableRow>
          </TableHeader>
          {loading ? (
            <TableBody className='border-[#343840]'>
              {new Array(10).fill(null).map(() => (
                <TableRow key={uniqueId('table_row_')} className='hover:bg-muted/10  border-[#343840]'>
                  {new Array(columnCount).fill(null).map(() => {
                    return (
                      <TableCell key={uniqueId('table_cell_')} className=' border-[#343840]'>
                        <Skeleton className='h-[21px] w-[140px]' />
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
     
          ) : (
            <TableBody>
              {data && data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columnCount} className='text-center'>
                    No Record available
                  </TableCell>
                </TableRow>
              ) : (
                data?.map((row) => (
                  <TableRow
                    key={row?.id ? row.id : uniqueId('table_row_')}
                    onClick={() => {
                      row.onClick?.();
                    }}
                    className={cn(
                      row.onClick
                        ? 'cursor-pointer bg-[#00001C] border-[#343840] hover:bg-muted/10'
                        : 'bg-[#00001C] border-[#343840] hover:bg-muted/10',
                    )}
                  >
                    {Object.keys(row.cells).map((cell, index) => {
                      const isVisible = columns.find((column) => column.id === cell)?.isVisible;
                      return (
                        (isVisible || cell === 'action') && (
                          <TableCell
                            key={uniqueId('table_cell_')}
                            align={columns[index]?.align}
                            className='bg-[#00001C] border-[#343840]'
                          >
                            {row.cells[cell]}
                          </TableCell>
                        )
                      );
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          )}
        </Tables>
      </div>
      {!loading && data && dataCount > itemsPerPage && (
        <Pagination
          id={tableID}
          currentPage={page}
          dataCount={dataCount}
          itemsPerPage={itemsPerPage}
          onChange={onPageChange}
          siblingNumber={siblingNumber}
        />
      )}
    </div>
  );
};

export default Table;
